import React, { useEffect, useMemo, useRef, useState } from "react";
import { groupBy, uniq } from "lodash";

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Card,
  CardHeader,
  CardContent,
  Chip,
  IconButton,
  Slide,
  Stack,
  Typography,
} from "@mui/material";
import { AddCircleOutline, Close, ExpandMore, OpenInNew } from "@mui/icons-material";
import Grid from "@mui/material/Grid2";
import { useTheme } from "@mui/material/styles";

import { HelpBox, SearchBox } from "@/components/atoms";
import { useBuilder, useBuilderActions } from "@/context";
import { useVariableSearch, useVariables } from "@/hooks";
import { Variable } from "@/types";
import VariableSearchSkeleton from "./VariableSearchSkeleton";
import { getTypeIcon } from "@/utils";

const VariableSearch: React.FC = () => {
  const { variables } = useVariables();
  const { selected, active } = useBuilder();
  const { selectVariables: onSelect, removeVariable: onDelete } = useBuilderActions();
  const theme = useTheme();

  const [searchText, setSearchText] = useState<string>("");
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});
  const [openCategories, setOpenCategories] = useState<Record<string, boolean>>({});
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [additionalResults, setAdditionalResults] = useState<Variable[]>([]);
  const { results: searchResults, loading } = useVariableSearch(searchText);

  const panelRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    setAdditionalResults([]);
  }, [searchText]);

  useEffect(() => {
    selectedCategory ?
      setAdditionalResults(variables.filter(x => x.tag_category === selectedCategory))
      : setAdditionalResults([]);
  }, [selectedCategory, variables]);

  useEffect(() => {
    if (!selectedCategory) return;

    const animationFrame = requestAnimationFrame(() => {
      if (panelRef.current) {
        panelRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    });

    return () => cancelAnimationFrame(animationFrame);

  }, [selectedCategory])

  useEffect(() => {
    const groups = uniq(searchResults.map(x => x.tag_type));
    setOpenGroups(Object.fromEntries(groups.map(x => [x, true])));
    const categories = uniq(searchResults.map(x => x.tag_category));
    setOpenCategories(Object.fromEntries(categories.map(x => [x, true])));
  }, [searchResults]);

  // Group results by tag_type
  const groupedResults = useMemo(() => {
    const groupData = groupBy(searchResults, 'tag_type');
    const groups = Object.keys(groupData);
    return { groups, groupData }
  }, [searchResults]);

  const handleSearchTextChange = (event: React.ChangeEvent<HTMLInputElement>) =>
    setSearchText(event.target.value || "");

  const handleCategoryClick = (event: React.MouseEvent, category: string, group: string) => {
    event.stopPropagation();
    setSelectedCategory(category);
    setOpenCategories({[category]: true});
    setOpenGroups({[group]: true});
  };

  const scrollToTop = () => {
    panelRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  }

  const handleGroupToggle = (group: string) => (_: React.SyntheticEvent, isExpanded: boolean) => {
    setOpenGroups((prev) => ({ ...prev, [group]: isExpanded }));
  };

  const handleCategoryToggle = (category: string) => (_: React.SyntheticEvent, isExpanded: boolean) => {
    setOpenCategories((prev) => ({ ...prev, [category]: isExpanded }));
  };

  const handleSelectCategoryVariables = (variables: Variable[]) => {
    // Isolate the variables that aren't selected already
    const updatedVariables = variables.filter(x => !isActive(x));

    // Add them
    onSelect(updatedVariables);
  }


  const isSelected = (variable: Variable) => {
    return selected.has(variable.tag_id);
  };

  const isActive = (variable: Variable) => {
    return active.has(variable.tag_id);
  }


  return (
    <>
      <Box mb={1}>
        <Typography variant="caption" >
          Search by variable name or category across the whole taxonomy. Results will be grouped by data source, and
          then by category. You can also expand a category to see other variables within it.
        </Typography>
      </Box>
      <Box height={500} ref={panelRef}>
        <Box pb={2}>
          <SearchBox
            value={searchText}
            onSearch={handleSearchTextChange}
            fullWidth={true}
            placeholder="Search for variables or categories"
            color="secondary"
          />
        </Box>
        {loading ? (
          <Grid size={12}>
            <VariableSearchSkeleton />
          </Grid>
        ) : (
          <Grid container size={12} spacing={3} ref={scrollRef} pr={2} sx={{ height: 444, overflowY: "auto", scrollBehavior: 'smooth' }}>
            <Grid
              size={additionalResults.length > 0 ? 8 : 12}
              sx={{
                transition: 'all 0.3s ease'
              }}
            >
              {groupedResults.groups.map((group, groupIdx) => {

                const categoryData = groupBy(groupedResults.groupData[group], 'tag_category');
                const categories = Object.keys(categoryData);

                return (
                  <Accordion
                    key={groupIdx}
                    variant="outlined"
                    expanded={!!openGroups[group]}
                    onChange={handleGroupToggle(group)}
                    sx={{ backgroundColor: "transparent" }}
                  >
                    <AccordionSummary expandIcon={<ExpandMore />}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        {getTypeIcon(group)}
                        <Typography>{group}</Typography>
                      </Stack>
                    </AccordionSummary>
                    <AccordionDetails>
                      {categories.map((category, categoryIdx) => {
                        return (
                          <Accordion
                            key={`${groupIdx}-${categoryIdx}`}
                            expanded={!!openCategories[category]}
                            onChange={handleCategoryToggle(category)}
                            slotProps={{
                              root: {
                                elevation: 4
                              }
                            }}
                          >
                            <AccordionSummary
                              expandIcon={<ExpandMore />}
                              sx={{
                                '& .MuiAccordionSummary-content': {
                                  margin: '0 !important', // Forces the margin to stay dead flat
                                },
                              }}
                            >
                              <Stack direction="row" spacing={1} alignItems="center">
                                <Typography variant="body2">{category}</Typography>
                                <HelpBox title="See other variables in this category">
                                  <IconButton
                                    sx={{
                                      borderRadius: "6px",
                                      color: theme.palette.divider
                                    }}
                                    onClick={(e) => handleCategoryClick(e, category, group)}
                                  >
                                    <OpenInNew fontSize="small" />
                                  </IconButton>
                                </HelpBox>
                              </Stack>
                            </AccordionSummary>
                            <AccordionDetails>
                              <Box pb={4}>
                                {categoryData[category].map((variable, variableIdx) => (
                                  <HelpBox
                                    title={
                                      isActive(variable) ? "Selected in the active query group"
                                        : isSelected(variable) ? "Selected in another query group"
                                          : `${variable.tag_type} | ${variable.tag_category} | ${variable.tag_name}`
                                    }
                                  >
                                    <Chip
                                      key={`${groupIdx}-${categoryIdx}-${variableIdx}`}
                                      size="small"
                                      color={isActive(variable) ? "secondary" : isSelected(variable) ? "info" : "default"}
                                      label={variable.tag_name}
                                      onClick={() => !isActive(variable) && onSelect([variable])}
                                      onDelete={isActive(variable) ? () => onDelete(variable) : undefined}
                                      sx={{ m: 0.5 }}
                                    />
                                  </HelpBox>
                                ))}
                              </Box>
                              <HelpBox title="Add all variables to the Query Group">
                                <IconButton
                                  sx={{
                                    position: "absolute",
                                    bottom: 8,
                                    right: 12,
                                    zIndex: 1,
                                    p: 0.5,
                                    borderRadius: "6px",
                                    color: theme.palette.divider,
                                  }}
                                  onClick={() => handleSelectCategoryVariables(categoryData[category])}
                                >
                                  <AddCircleOutline fontSize="small" />
                                </IconButton>
                              </HelpBox>
                            </AccordionDetails>
                          </Accordion>
                        )}
                      )}
                    </AccordionDetails>
                  </Accordion>
                )
              })}
            </Grid>
            {additionalResults.length > 0 && (
              <Grid size={4}>
                <Slide
                  direction="left"
                  in={additionalResults.length > 0}
                  mountOnEnter
                  unmountOnExit
                  timeout={300}
                  onEntered={scrollToTop}
                >
                  <Card
                    variant="outlined"
                    sx={{
                      position: "relative",
                      backgroundColor: "transparent"
                  }}
                  >
                    <CardHeader
                      title={
                        <Box display="flex" flexDirection="row" alignItems="center" justifyContent="space-between">
                          {selectedCategory && <Typography>{`Other ${selectedCategory}`}</Typography>}
                          <IconButton
                            sx={{
                              borderRadius: "6px"
                            }}
                            onClick={() => setSelectedCategory(null)}
                          >
                            <Close fontSize="small" />
                          </IconButton>
                        </Box>
                      }
                      disableTypography
                      slotProps={{ title: { variant: "body1" } }}
                    />
                    <CardContent>
                      <Box pb={4}>
                        {additionalResults.map((variable, variableIdx) => (
                          <HelpBox
                            title={
                              isActive(variable) ? "Selected in the active query group"
                                : isSelected(variable) ? "Selected in another query group"
                                  : `${variable.tag_type} | ${variable.tag_category} | ${variable.tag_name}`
                            }
                          >
                            <Chip
                              key={`additional-${variableIdx}`}
                              size="small"
                              color={isActive(variable) ? "secondary" : isSelected(variable) ? "info" : "default"}
                              label={variable.tag_name}
                              onClick={() => !isActive(variable) && onSelect([variable])}
                              onDelete={isActive(variable) ? () => onDelete(variable) : undefined}
                              sx={{ m: 0.5 }}
                            />
                          </HelpBox>
                        ))}
                      </Box>
                      <HelpBox title="Add all variables to the Query Group">
                        <IconButton
                          sx={{
                            position: "absolute",
                            bottom: 8,
                            right: 12,
                            zIndex: 1,
                            p: 0.5,
                            borderRadius: "6px",
                            color: theme.palette.divider,
                          }}
                          // onClick={() => onSelectMulti(categoryData[category])}
                        >
                          <AddCircleOutline fontSize="small" />
                        </IconButton>
                      </HelpBox>
                    </CardContent>
                  </Card>
                </Slide>
              </Grid>
            )}
          </Grid>
        )}
      </Box>
    </>
  );
};

export default VariableSearch;
